import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore'
import { db, ensureAuthUser, isFirebaseConfigured } from './firebase'
import { toISODate } from './lib/exercise'

const STORAGE_KEY = 'military-calisthenics-trainer-state'

// Antes del fix, generatePlan() armaba las claves de fecha con
// Date.toISOString() (UTC) mientras el resto de la app usa toISODate()
// (hora local). Para usuarios fuera de UTC+0 eso desalineaba el plan
// guardado respecto al calendario real. Esto recalcula el corrimiento a
// partir de profile.createdAt y reescribe las claves de plan/completion
// una sola vez para los datos guardados antes del fix.
function computeShiftDays(createdAt) {
  if (!createdAt) {
    return 0
  }

  const created = new Date(createdAt)
  if (Number.isNaN(created.getTime())) {
    return 0
  }

  const localKey = toISODate(created)
  const utcKey = created.toISOString().split('T')[0]

  if (localKey === utcKey) {
    return 0
  }

  const diffMs = Date.parse(`${utcKey}T00:00:00Z`) - Date.parse(`${localKey}T00:00:00Z`)
  return Math.round(diffMs / 86400000)
}

function shiftDateKey(dateKey, days) {
  const shifted = new Date(`${dateKey}T00:00:00Z`)
  shifted.setUTCDate(shifted.getUTCDate() - days)
  return shifted.toISOString().split('T')[0]
}

function migratePlanDates(profile, plan, completion) {
  const shiftDays = computeShiftDays(profile.createdAt)
  if (shiftDays === 0) {
    return { plan, completion }
  }

  const migratedPlan = {}
  for (const [date, dayPlan] of Object.entries(plan)) {
    migratedPlan[shiftDateKey(date, shiftDays)] = dayPlan
  }

  const migratedCompletion = {}
  for (const [key, value] of Object.entries(completion)) {
    const separatorIndex = key.indexOf('__')
    if (separatorIndex === -1) {
      migratedCompletion[key] = value
      continue
    }
    const date = key.slice(0, separatorIndex)
    const rest = key.slice(separatorIndex)
    migratedCompletion[`${shiftDateKey(date, shiftDays)}${rest}`] = value
  }

  return { plan: migratedPlan, completion: migratedCompletion }
}

export function loadSavedState() {
  const raw = localStorage.getItem(STORAGE_KEY)

  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw)
    if (!parsed.profile) {
      return null
    }

    const defaultWeeklyConfig = {
      targetSessions: parsed.profile.daysPerWeek,
      reminderTime: '06:00',
      reminderText: 'Prepara uniforme, agua y rutina del dia.',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      notificationsEnabled: false,
    }

    const weeklyConfig = {
      ...defaultWeeklyConfig,
      ...parsed.weeklyConfig,
    }

    let plan = parsed.plan ?? {}
    let completion = parsed.completion ?? {}
    const subscription = parsed.subscription ?? { active: false }
    const challenges = parsed.challenges ?? {}

    if (!parsed.planDatesFixed) {
      const migrated = migratePlanDates(parsed.profile, plan, completion)
      plan = migrated.plan
      completion = migrated.completion
      void saveState(parsed.profile, plan, completion, weeklyConfig, subscription, challenges)
    }

    return {
      profile: parsed.profile,
      plan,
      completion,
      weeklyConfig,
      subscription,
      challenges,
    }
  } catch {
    return null
  }
}

export function clearSavedState() {
  localStorage.removeItem(STORAGE_KEY)
}

export async function saveState(profile, plan, completion, weeklyConfig, subscription = { active: false }, challenges = {}) {
  const payload = { profile, plan, completion, weeklyConfig, subscription, challenges, planDatesFixed: true }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))

  if (!isFirebaseConfigured || !db) {
    return
  }

  // Si la autenticacion de Firebase tarda o se cuelga (red lenta, popup
  // bloqueado, etc.), no queremos que la UI quede esperando para siempre en
  // "Guardando...". Despues de 8s seguimos solo con el localStorage ya
  // escrito arriba; ensureAuthUser() sigue resolviendo en segundo plano para
  // el proximo intento.
  const uid = await Promise.race([
    ensureAuthUser(),
    new Promise((resolve) => setTimeout(() => resolve(null), 8000)),
  ])
  if (!uid) {
    return
  }

  // plan y subscription no se mandan: solo los escriben las Cloud Functions
  // (firestore.rules los rechaza desde el cliente). updateDoc reemplaza cada
  // campo de primer nivel completo, asi un completion vacio tras reiniciar el
  // plan no se mezcla con el anterior; si el documento todavia no existe se
  // crea con setDoc.
  const remotePayload = {
    profile,
    completion,
    weeklyConfig,
    challenges,
    planDatesFixed: true,
    updatedAt: serverTimestamp(),
  }
  const athleteRef = doc(db, 'athletes', uid)

  try {
    await updateDoc(athleteRef, remotePayload)
  } catch (error) {
    if (error?.code !== 'not-found') {
      throw error
    }
    await setDoc(athleteRef, remotePayload)
  }
}

async function sha256Hex(text) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

// Convierte una hora local HH:MM a UTC, redondeada al bloque de 5 minutos
// en el que corre sendScheduledWorkoutNotifications.
export function toReminderTimeUtc(localTime) {
  const [hours, minutes] = localTime.split(':').map(Number)
  const date = new Date()
  date.setHours(hours, Math.floor(minutes / 5) * 5, 0, 0)
  return `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`
}

export async function saveReminderSettings({ notificationsEnabled, reminderTime, reminderText }, fcmToken) {
  if (!db) {
    return
  }

  const uid = await ensureAuthUser()
  if (!uid) {
    return
  }

  if (notificationsEnabled && fcmToken) {
    const tokenId = await sha256Hex(fcmToken)
    await setDoc(doc(db, 'athletes', uid, 'notificationTokens', tokenId), {
      token: fcmToken,
      platform: 'android',
      updatedAt: serverTimestamp(),
    })
  }

  await setDoc(
    doc(db, 'athletes', uid, 'settings', 'reminders'),
    {
      notificationsEnabled: Boolean(notificationsEnabled),
      reminderTimeUtc: toReminderTimeUtc(reminderTime),
      reminderText: String(reminderText ?? '').slice(0, 120),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
}

export async function fetchRemoteState(uid) {
  if (!db) {
    return null
  }

  const snap = await getDoc(doc(db, 'athletes', uid))
  if (!snap.exists()) {
    return null
  }

  const data = snap.data()
  if (!data.profile) {
    return null
  }

  return {
    profile: data.profile,
    plan: data.plan ?? {},
    completion: data.completion ?? {},
    weeklyConfig: data.weeklyConfig,
    subscription: data.subscription ?? { active: false },
    challenges: data.challenges ?? {},
  }
}
